import type { VotingLocationWithAssignments } from 'admin/src/components/Pollworker/Panels/pollworkerVotingLocationDetailsUtils';
import type { WorkConfirmationStatusValue } from 'admin/src/components/Pollworker/WorkConfirmationStatus';
import type { ActionTemplate, CampaignFinanceMergeFixtures, CustomerTemplate, PollworkerMergeWorker } from 'admin/src/components/ui/EmailTemplateBuilder/types';
import type { ConsumableCreatePayload } from 'admin/src/screens/admin/EasyOrder/AddEditInventoryItemSheet';
import type { PollworkerToImport } from 'admin/src/screens/admin/Pollworker/Import';
import type { PollworkerAssignmentReportDTO } from 'admin/src/screens/admin/Pollworker/PollworkerAssignmentReportDataGridExport';
import type { PollworkerScheduleReportDTO } from 'admin/src/screens/admin/Pollworker/PollworkerSchedulesDataGridExport';
import type { PollworkerClassScheduleReportDTO } from 'admin/src/screens/admin/Pollworker/PollworkerTrainingSchedulesDataGridExport';
import type {
  AssignPollworkersToElectionPayload,
  ConsumableItem,
  ConsumableItemInventoryDetailsDTO,
  CustomerBlog,
  CustomField,
  CustomFieldData,
  Election,
  EmailTemplateResponse,
  Expense,
  Party,
  PollWorker,
  PollworkerApiResponse,
  PollworkerContact,
  PollworkerFlag,
  PollWorkerInfo,
  PollworkerLog,
  PollworkerReimbursementStatus,
  PollworkerRequirement,
  PollworkerSettings,
  PollworkerTimeClockAction,
  PollworkerUserInfo,
  PollworkerWorkAssignmentConfirmationsForElection,
  PollworkerWorkHistory,
  Precinct,
  RoleInfo,
  Schedule,
  TrainingClass,
  TrainingProgress,
  Vendor,
  VotingLocation,
  VotingLocationSchedule,
  WorkerApplication,
} from 'admin/src/types';
import type { CartItem } from 'admin/src/types/CartItem';
import type { DataburstResponse } from 'admin/src/types/DataburstResponse';
import type { FilterModel, SortModel } from 'admin/src/types/Filter';
import type { InventoryEquipTypeBlacklist } from 'admin/src/types/Inventory/InventoryEquipTypeBlacklist';
import type { Order, OrderHistoryPayload, OrderItem, OrderSearchConfig } from 'admin/src/types/Order';
import type { TimeBlockResponseItem } from 'admin/src/types/Pollworker/PollworkerCourses';
import type { PollworkerEmailQueryParams } from 'admin/src/types/Pollworker/PollworkerEmails';
import type { PollworkerMessage } from 'admin/src/types/Pollworker/PollworkerWorkHistory';
import type { TwilioMessages } from 'admin/src/types/TwilioMessage';
import type { CurrentUserSettingsDTO, OrderConfig, UserLoginSuccessDTO } from 'admin/src/types/User';
import Cache, { imageCacheKey, imageCachePrefix } from 'admin/src/utils/cache';
import dayjs from 'dayjs';
import { DEFAULT_EMAIL_ID } from 'shared/src/constants';
import { fetchApi, fetchApiWithAbort, fetchApiWithRetry, getAuthHeaders, getHeaders } from 'shared/src/fetchers';
import { convertFileToByteArray } from 'shared/src/utils/converters';
import { fetchEmployees } from './setupAndAdmin';
import type { RescheduleWorkersValidationResult } from 'admin/src/screens/admin/Pollworker/DialogPollworkerRescheduleAttendee';

export { getHeaders, fetchEmployees, fetchApiWithRetry };

export * from './featureFlags';
export * from './inventory';
export * from './pollworker';
export * from './roles';
export * from './tracker';
export * from './setupAndAdmin';

export function safeAtob(input: string): string | null {
  try {
    return atob(input);
  } catch {
    return null;
  }
}

export function safeParse<T>(input: string): T | null {
  const decoded = safeAtob(input);
  if (!decoded) return null;

  try {
    return JSON.parse(decoded) as T;
  } catch {
    return null;
  }
}

export async function json<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed with status ${response.status}`);
  }
  return response.json();
}

export function getClassesForElection(customerId: string, electionId: string) {
  return fetchApi('/api/GetClassesForElection', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ id: customerId, electionId }),
  }).then((resp) => resp.json());
}

export function addAttendeeToClassSession(evUserId: string, sessionId: string) {
  return fetchApi('/api/AddAttendeeToClassSession', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      EVUserId: evUserId,
      SessionId: sessionId,
      compress: false,
    }),
  }).then((resp) => resp.json());
}

export function loadPollworkerExpenses(electionId: string, evUserId: string) {
  return fetchApi('/api/PollworkerReimbursementApi/LoadPollworkerReimbursementsByParameter', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      Id: evUserId,
      ElectionId: electionId,
      compress: false,
    }),
  }).then((resp) => resp.json());
}

export function loadPollworkerExpensesV3(electionId: string, evUserId: string): Promise<Expense[]> {
  return fetchApi(`/api/v3/pollworker/expenses/${electionId}/${evUserId}`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export async function markExpenseStatus(expenses: Partial<Expense>[], status: PollworkerReimbursementStatus, electionId: string) {
  const expenseIds = expenses.map((e) => e.id);

  return fetchApi(`/api/v3/pollworker/expenses/${electionId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ Ids: expenseIds, Status: status }),
  }).then((resp) => resp.json());
}

export async function markWorkExpenseStatus(expenses: Partial<Expense>[], status: PollworkerReimbursementStatus) {
  const expenseIds = expenses.map((e) => e.id);

  return fetchApi('/api/PollworkerScheduleApi/SavePollworkerScheduleStatus', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ IdLIst: expenseIds, StatusValue: status }),
  }).then((resp) => resp.json());
}

export async function markTrainingExpenseStatus(expenses: Partial<Expense>[], status: PollworkerReimbursementStatus, electionId: string) {
  const attendeeIds = expenses.map((e) => e.id);

  return fetchApi(`/api/v3/pollworker/expenses/training/${electionId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ Ids: attendeeIds, Status: status }),
  }).then((resp) => resp.json());
}

export async function getElectionsForCustomer(): Promise<Election[]> {
  const resp = await fetchApi('/api/v3/elections', {
    method: 'GET',
    headers: getHeaders(),
  });

  if (resp.ok) {
    return resp.json();
  }

  return [];
}

export async function getSupportingElectionData(id: string): Promise<DataburstResponse<string>> {
  return fetchApi('/api/ElectionApi/LoadSupportingDataForElection', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ Id: id, Notes: '', Compress: false }),
  }).then((resp) => resp.json());
}

export async function getPollworkerSettings(customerId: string): Promise<PollworkerSettings> {
  return fetchApi('/api/SettingsApi/LoadPollworkerSettings', {
    method: 'POST',
    body: JSON.stringify({ Id: customerId, Compress: false }),
  })
    .then((resp) => resp.json())
    .then((resp) => JSON.parse(atob(resp.data)));
}

export async function updatePollworkerSettings(customerId: string, settings: PollworkerSettings): Promise<PollworkerSettings> {
  return fetchApi(`/tables/Setting_Pollworker/${customerId}`, {
    method: 'PATCH',
    body: JSON.stringify(settings),
  }).then((resp) => resp.json());
}

export async function createPollworkerExpense(expense: Partial<Expense>) {
  return fetchApi('/tables/PollworkerReimbursement/PostPollworkerReimbursement', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(expense),
  }).then((resp) => resp.json());
}

interface PollworkerExpenseRequest {
  keyElectionId: Expense['keyElectionId'];
  keyEVUserId: Expense['keyEVUserId'];
  chargeAmount?: Expense['chargeAmount'];
  mileage?: Expense['mileage'];
  notes?: Expense['notes'];
  expenseDate?: Expense['expenseDate'];
  id?: Expense['id'];
}

export async function createPollworkerExpenseV3(request: PollworkerExpenseRequest) {
  const { keyElectionId, keyEVUserId, ...body } = request;

  return fetchApi(`/api/v3/pollworker/expenses/${keyElectionId}/${keyEVUserId}`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  }).then((resp) => resp.json());
}

export function updateCustomField(customFieldId: string, customFields: { [key: string]: string | number | boolean | null }) {
  return fetchApi('/api/CustomFieldContentApi/SaveCustomFieldContent', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ Id: customFieldId, Data: customFields }),
  }).then((resp) => resp.json());
}

export function patchPollworkerExpense(expense: Partial<Expense>) {
  return fetchApi(`/tables/PollworkerReimbursement/PatchPollworkerReimbursement/${expense.id}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(expense),
  }).then((resp) => resp.json());
}

export async function patchPollworkerExpenseV3(expense: PollworkerExpenseRequest) {
  const { keyElectionId, keyEVUserId, id, ...body } = expense;

  return fetchApi(`/api/v3/pollworker/expenses/${keyElectionId}/${keyEVUserId}/${id}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(body),
  }).then((resp) => resp.json());
}

export async function deletePollworkerExpenseV3(electionId: string, evUserId: string, expenseId: string): Promise<void> {
  await fetchApi(`/api/v3/pollworker/expenses/${electionId}/${evUserId}/${expenseId}`, {
    method: 'DELETE',
  });
}

export function pollworkerClockInOut(pollworkerWorkHistoryId: string) {
  return fetchApi('/api/PollworkerTimeClockActionApi/PollworkerClockInOut', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ Id: pollworkerWorkHistoryId, Notes: '' }),
  }).then((resp) => resp.json());
}

export function createPollworkerSchedule(schedule: Partial<Schedule>) {
  return fetchApi('/tables/PollworkerSchedule/PostPollworkerSchedule', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(schedule),
  }).then((resp) => resp.json());
}

export function patchPollworkerSchedule(schedule: Partial<Schedule>) {
  return fetchApi(`/tables/PollworkerSchedule/PatchPollworkerSchedule/${schedule.id}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(schedule),
  }).then((resp) => resp.json());
}

export function deletePollworkerSchedule(schedule: Partial<Schedule>) {
  return fetchApi(`/tables/PollworkerSchedule/DeletePollworkerSchedule/${schedule.id}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
}

export function createPollworkerClockInOut(timeClockAction: Partial<PollworkerTimeClockAction>) {
  return fetchApi('/tables/PollworkerTimeClockAction/PostPollworkerTimeClockAction', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(timeClockAction),
  }).then((resp) => resp.json());
}

export function assignToElection(payload: AssignPollworkersToElectionPayload) {
  return fetchApi('/api/AddPollworkersToElection', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  }).then((resp) => resp.json());
}

export function patchPollworkerClockInOut(timeClockAction: Partial<PollworkerTimeClockAction>, timeClockId: string) {
  return fetchApi(`/tables/PollworkerTimeClockAction/PatchPollworkerTimeClockAction/${timeClockId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(timeClockAction),
  }).then((resp) => resp.json());
}

export function deletePollworkerClockInOut(timeClockId: string) {
  return fetchApi(`/tables/PollworkerTimeClockAction/DeletePollworkerTimeClockAction/${timeClockId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  }).then((resp) => resp.json());
}

export function getPollworkerTimeEntriesForElection(electionId: string, pollworkerEvUserIds: string[]) {
  return fetchApi('/api/PollworkerTimeClockActionApi/LoadPollworkerTimeEntriesForElection', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      Id: electionId,
      List: pollworkerEvUserIds,
      Compress: false,
    }),
  }).then((resp) => resp.json());
}

export function removeAttendeeFromClassSession(sessionId: string) {
  return fetchApi(`/tables/PollworkerClassAttendee/${sessionId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  }).then((resp) => resp.json());
}

export function getPollworkerClassMatrix(trainingClassId: string, electionId: string) {
  return fetchApi('/api/GetPollworkerClassMatrixData', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ id: trainingClassId, electionId, compress: false }),
  }).then((resp) => resp.json());
}

export function getPollworkerRequiredClassesToBeToken(keyEvUserId: string, electionId: string) {
  return fetchApi('/api/PollworkerApi/LoadRequiredClassesToBeTaken', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ id: keyEvUserId, electionId }),
  }).then((resp) => resp.json());
}

export function getPollworkerTrainingClassSessionList(electionId: string, classId: string) {
  return fetchApi('/api/getPollworkerTrainingClassSessionList', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ id: classId, ElectionId: electionId }),
  }).then((resp) => resp.json());
}

export function savePollworkerRequirement(requirement: Partial<PollworkerRequirement>) {
  return fetchApi('/tables/PollworkerRequirement', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(requirement),
  }).then((resp) => resp.json());
}

export function deletePollworkerRequirement(requirementId: string) {
  return fetchApi(`/tables/PollworkerRequirement/${requirementId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  }).then((resp) => resp.json());
}

export function updatePollworkerRequirement(requirement: Partial<PollworkerRequirement>) {
  return fetchApi(`/tables/PollworkerRequirement/${requirement.id}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(requirement),
  }).then((resp) => resp.json());
}

export function getDocumentsForObject(id: string) {
  return fetchApi('/api/DocumentsApi/LoadDocumentsForObject', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ Id: id }),
  })
    .then((resp) => resp.json())
    .then(({ data }) => JSON.parse(atob(data)));
}

export async function legacyUploadFileToStorage(url: string, data: string | number[]) {
  return fetchApi('/api/BlobStorageApi/UploadDataFileToStorage', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ Id: url, Data: data, Compress: false }),
  }).then((resp) => resp.json());
}

export async function uploadFileToStorage(url: string, file: File) {
  const host = import.meta.env.VITE_API_V3_DOMAIN;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileName', url);
  const headers = getAuthHeaders();

  return fetch(`${host}/api/v3/file-storage/upload`, {
    method: 'POST',
    headers,
    body: formData,
  }).then((resp) => resp.json());
}

export async function retrieveElectionDataFileFromSecureStorage(url: string) {
  return fetchApi('/api/BlobStorageApi/RetrieveElectionDataFileFromSecureStorage', {
    headers: getHeaders(),
    method: 'POST',
    body: JSON.stringify({
      Id: url,
      Compress: false,
    }),
  }).then((resp) => resp.json());
}

export async function uploadFileToElectionStorage(electionId: string, data: File) {
  const fileToUpload = await convertFileToByteArray(data);

  return fetchApi('/api/BlobStorageApi/UploadElectionDocumentsToSecureStorage', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      id: electionId,
      documentName: data.name,
      data: fileToUpload,
    }),
  }).then((resp) => resp.json());
}

export async function retrieveCampaignFinanceFilerDataFileFromSecureStorage(url: string) {
  return fetchApi('/api/BlobStorageApi/RetrieveCampaignFinanceFilerDataFileFromSecureStorage', {
    headers: getHeaders(),
    method: 'POST',
    body: JSON.stringify({
      Id: url,
      Compress: false,
    }),
  }).then((resp) => resp.json());
}

export async function uploadFileToCampaignFinanceFilerStorage(filerId: string, data: File) {
  const fileToUpload = await convertFileToByteArray(data);

  return fetchApi('/api/BlobStorageApi/UploadCampaignFinanceFilerDocumentsToSecureStorage', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      id: filerId,
      documentName: data.name,
      data: fileToUpload,
    }),
  }).then((resp) => resp.json());
}

export async function uploadLocationImage(locationId: string, file: File): Promise<DataburstResponse<boolean>> {
  const fileToUpload = await convertFileToByteArray(file);
  const body = {
    id: locationId,
    data: fileToUpload,
  };

  const response = await fetchApi('/api/BlobStorageApi/UploadLocationImageToSecureStorage', {
    method: 'POST',
    body: JSON.stringify(body),
  });

  return (await response.json()) as DataburstResponse<boolean>;
}

export async function removeLocationImage(locationId: string): Promise<DataburstResponse<boolean>> {
  const response = await fetchApi('/api/BlobStorageApi/RemoveLocationImageFromSecureStorage', {
    method: 'DELETE',
    body: JSON.stringify(locationId),
  });

  return (await response.json()) as DataburstResponse<boolean>;
}

export async function getLocationImageFromSecureStorage(locationId: string): Promise<DataburstResponse<{ data: string }>> {
  const resp = await fetchApi('/api/BlobStorageApi/RetrieveLocationImageFromSecureStorage', {
    method: 'POST',
    body: JSON.stringify({
      Id: locationId,
      Compress: false,
    }),
  });

  const databurstResponse = (await resp.json()) as DataburstResponse<string>;

  return {
    ...databurstResponse,
    data: databurstResponse.data ? (JSON.parse(atob(databurstResponse.data)) as { data: string }) : { data: '' },
  };
}

export function createDocument(document: { url: string; objectId: string; documentName: string }) {
  return fetchApi('/tables/Document/PostDocument', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(document),
  }).then((resp) => resp.json());
}

export function deleteDocument(documentId: string) {
  return fetchApi(`/tables/Document/${documentId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
}

export function deletePollworkerEmergencyContact(emergencyContactId: string) {
  return fetchApi(`/tables/EmergencyContact/DeleteEmergencyContact/${emergencyContactId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  }).then((resp) => resp.json());
}

export function savePollworkerCallLog(pollworkerId: string, notes: string, reason: number, keyElectionId?: string) {
  return fetchApi('/api/v3/pollworker/logs', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      keyEVUserId: pollworkerId,
      notes,
      reasonId: reason,
      keyElectionId: keyElectionId ?? null,
    }),
  }).then((resp) => {
    if (resp.ok) {
      return resp.json();
    }
    throw new Error('Error creating worker log');
  });
}

export function updatePollworkerLog(id: string, payload: { notes: string; reasonId: number }) {
  return fetchApi(`/api/v3/pollworker/logs/${id}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  }).then((resp) => {
    if (resp.ok) {
      return resp.json();
    }
    throw new Error('Update failed');
  });
}

export function deletePollworkerCallLog(log: PollworkerLog) {
  return fetchApi(`/api/v3/pollworker/logs/${log.id}`, {
    method: 'DELETE',
    headers: getHeaders(),
  }).then((resp) => {
    if (!resp.ok) throw new Error('Delete failed');
    return null;
  });
}

export function createPollworkerEmergencyContact(contact: Partial<PollworkerContact>) {
  return fetchApi('/tables/EmergencyContact/PostEmergencyContact', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(contact),
  }).then((resp) => resp.json());
}

export function patchPollworkerEmergencyContact(contact: Partial<PollworkerContact>) {
  return fetchApi(`/tables/EmergencyContact/PatchEmergencyContact/${contact.id}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(contact),
  }).then((resp) => resp.json());
}

export function saveSkillRating(keyElectionId: string, keyEVUserId: string, keySkillId: string, rating: number) {
  return fetchApi('/tables/PollworkerSkillRating', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ keyElectionId, keyEVUserId, keySkillId, rating }),
  }).then((resp) => resp.json());
}

export function deleteSkillRating(id: string) {
  return fetchApi(`/tables/PollworkerSkillRating/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
  }).then((resp) => resp.json());
}

export function getRequirementClasses(keyCustomerId: string) {
  return fetchApi(`/tables/PollworkerTrainingClass?$filter=${encodeURIComponent(`(KeyCustomerId eq '${keyCustomerId}')`)}`, {
    method: 'GET',
    headers: getHeaders(),
  }).then((resp) => resp.json());
}

export function updateSkillRating(id: string, keyElectionId: string, keyEVUserId: string, keySkillId: string, rating: number, dateRated: string) {
  return fetchApi(`/tables/PollworkerSkillRating/${id}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({
      id,
      keyElectionId,
      keyEVUserId,
      keySkillId,
      rating,
      dateRated,
    }),
  }).then((resp) => resp.json());
}

export function loadRequirementsListForElectionAndUser(userId: string, electionId: string) {
  return fetchApi('/api/LoadRequirementsListForElectionAndUser', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ Id: userId, electionId }),
  }).then((resp) => resp.json());
}

export function loadPollworkerLogsWithCustomerId(payload: { id?: string; startDate: string; endDate: string }): Promise<PollworkerLog[]> {
  const params = new URLSearchParams({
    startDate: payload.startDate,
    endDate: payload.endDate,
  });

  if (payload.id) {
    params.append('Id', payload.id);
  }

  return fetchApi(`/api/v3/pollworker/logs?${params.toString()}`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

// TODO: Insecure api
export function loadPollworkerSkills(electionId: string, userId: string) {
  return fetchApi(`/tables/PollworkerSkillRating?$filter=${encodeURIComponent(`((KeyElectionId eq '${electionId}') and (KeyEVUserId eq '${userId}'))`)}`, {
    method: 'GET',
    headers: getHeaders(),
  }).then((resp) => resp.json());
}

export function loadPollworkerContacts(userId: string) {
  return fetchApi('/api/EmergencyContactsApi/LoadContactsForUser', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ Id: userId, Compress: false }),
  }).then((resp) => resp.json());
}

export function login(username: string, password: string): Promise<UserLoginSuccessDTO | string> {
  return fetchApi('/api/EVLogin', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ Username: username, Password: password }),
  }).then(async (response) => {
    const body = await response.json();
    if (typeof body === 'string') {
      return body;
    }
    return body as UserLoginSuccessDTO;
  });
}

export async function getCustomerOrders(): Promise<Order[]> {
  return json(await fetchApi('/api/v3/eo/orders', { method: 'GET' }));
}

export async function getAllOrders(): Promise<Order[]> {
  return json(await fetchApi('/api/v3/eo/orders/jurisdictions', { method: 'GET' }));
}

export function getOrderById(orderId: string): [Promise<Order>, AbortController] {
  const [requestPromise, controller] = fetchApiWithAbort(`/api/v3/eo/orders/${orderId}`, { method: 'GET' });

  return [requestPromise.then((response: Response) => response.json()), controller];
}

export async function getOrderSearchConfig(): Promise<OrderSearchConfig> {
  return json(await fetchApi('/api/v3/eo/orders/search-config', { method: 'GET' }));
}

export async function createOrder(): Promise<Order> {
  return await fetchApi('/api/v3/eo/orders/create', {
    method: 'POST',
    body: JSON.stringify({}),
  }).then((response: Response) => response.json());
}

export async function getOrderHistory(sortModel: SortModel[], filterModels: { [key: string]: FilterModel }, page: number): Promise<OrderHistoryPayload> {
  const body = JSON.stringify({ sortModel, filterModels, startRow: page, endRow: 0 });
  return json(await fetchApi('/api/v3/eo/orders/history', { method: 'POST', body }));
}

export async function submitOrder(notes: string) {
  await fetchApi('/api/v3/eo/orders', {
    method: 'POST',
    body: JSON.stringify({ notes }),
  }).then((response: Response) => response.json());
}

export async function updateOrderItemQuantity(orderId: string, orderItemId: string, quantity: number): Promise<void> {
  await fetchApi(`/api/v3/eo/orders/${orderId}/items/${orderItemId}`, {
    method: 'PATCH',
    body: JSON.stringify({ quantity }),
  }).then((response: Response) => response.json());
}

export async function removeOrderItem(orderId: string, orderItemId: string): Promise<void> {
  await fetchApi(`/api/v3/eo/orders/${orderId}/items/${orderItemId}`, { method: 'DELETE' }).then((response: Response) => response.json());
}

export async function updateOrderAsShipped(order: Order, shippingNotes: string): Promise<void> {
  await fetchApi(`/api/v3/eo/orders/${order.id}`, {
    method: 'PATCH',
    body: JSON.stringify({ ...order, shippingNotes }),
  }).then((response: Response) => response.json());
}

export async function updateOrderStatus(orderId: string, status: number) {
  await fetchApi('/api/v3/eo/orders/status', {
    method: 'PATCH',
    body: JSON.stringify({ orderId, status }),
  }).then((response: Response) => response.json());
}

export async function getConsumables(): Promise<ConsumableItem[]> {
  return json(await fetchApi('/api/v3/eo/inventory/items?consumable=true', { method: 'GET' }));
}

export async function getConsumable(itemId: string): Promise<ConsumableItem> {
  return json(await fetchApi(`/api/v3/eo/inventory/items/${itemId}`, { method: 'GET' }));
}

export async function addConsumables(items: ConsumableCreatePayload[]): Promise<ConsumableItem[]> {
  return await fetchApi('/api/v3/eo/inventory/items', {
    method: 'POST',
    body: JSON.stringify({ items: items }),
  }).then((response: Response) => response.json());
}

export async function saveConsumable(item: ConsumableItem) {
  return fetchApi(`/api/v3/eo/inventory/items/${item.id}`, {
    headers: getHeaders(),
    method: 'PATCH',
    body: JSON.stringify({ ...item }),
  }).then((response: Response) => response.json());
}

export async function getJurisdictions(): Promise<OrderConfig[]> {
  return json(await fetchApi('/api/v3/eo/jurisdictions', { method: 'GET' }));
}

export async function updateJurisdiction(jurisdiction?: OrderConfig) {
  const json = JSON.stringify(jurisdiction);

  return await fetchApi('/api/v3/eo/jurisdiction', {
    method: 'PATCH',
    body: json,
  });
}

export async function getOrder(): Promise<Order> {
  return json(await fetchApi('/api/v3/eo/orders/order', { method: 'GET' }));
}

export async function addItemToOrder(item: ConsumableItem, quantity: number): Promise<OrderItem> {
  return await fetchApi(`/api/v3/eo/orders/order/${item.id}`, {
    method: 'POST',
    body: JSON.stringify({ itemId: item.id, quantity }),
  }).then((response: Response) => response.json());
}

export async function removeFromOrder(item: CartItem): Promise<void> {
  const response = await fetchApi(`/api/v3/eo/orders/order/items/${item.id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Request failed with status ${response.status}`);
  }
}

export type EasyOrderQuickStats = {
  activeCarts: number;
  top5Items: EasyOrderTopItem[];
  lowInventoryItems: ConsumableItem[];
  orderCountsByWeek: EasyOrderOrderCountByWeek[];
  activeItemsMissingReorderThreshold: ConsumableItem[];
};

export type EasyOrderTopItem = {
  keyInventoryItemId: string;
  quantitySum: number;
  inventoryEquipType: ConsumableItem;
};

export type EasyOrderOrderCountByWeek = {
  year: number;
  week: number;
  orderCount: number;
};

export type EasyOrderInventoryEquipType = {
  id: string;
  keyCustomerId: string;
  equipTypeName: string;
  needsTesting: boolean;
  daysBetweenTests: number;
  daysBeforeElection: number;
  active: boolean;
  requiresChainOfCustody: boolean;
  hasUniqueId: boolean;
  uniqueIdMask: string;
  uniqueIdLastNumber: number;
  countOnHand: number;
  version: string;
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
  countOnHandReorderThreshold: number;
  labelTemplateId: string | null;
  requiresSealForPackage: boolean;
  packageLocationAssignmentMode: number;
  accessoryOnly: boolean;
  allowEarlyReturn: boolean;
  usedInWaitTimeCalc: boolean;
  returnSealEquipTypeId: string;
  outGoingSealEquipTypeId: string;
  outgoingSealCount: number;
  returnSealCount: number;
  threshholdAlertEmailList: string | null;
  threshholdAlertEmailBody: string | null;
  parentCustomerId: string;
  followsElectionStorageCycle: boolean;
  description: string;
  keyInventoryEquipTypeVendorId: string | null;
  isExternalVendor: boolean;
  allowOverPurchasing: boolean;
  storageLocationAllowedTypes: unknown | null;
  inventoryEquipments: unknown | null;
  inventoryLNATests: unknown | null;
  inventoryEquipmentTypePackageSteps: unknown | null;
  inventoryEquipTypePackageRequirements: unknown | null;
  inventoryEquipTypeVendor: unknown | null;
};

export async function getStats() {
  return json<EasyOrderQuickStats>(await fetchApi('/api/v3/eo/inventory/items/stats', { method: 'GET' }));
}

export async function getInventoryLevels(): Promise<ConsumableItemInventoryDetailsDTO[]> {
  const response = await fetchApi('/api/v3/eo/inventory/items/details', { method: 'GET' });
  return await json(response);
}

export function getInventoryItemDetails(itemId: string): [Promise<Order[]>, AbortController] {
  const [requestPromise, controller] = fetchApiWithAbort(`/api/v3/eo/inventory/items/${itemId}/orders`, { method: 'GET' });

  return [requestPromise.then((response: Response) => response.json()), controller];
}

export async function deleteConsumable(itemId: string) {
  const response = await fetchApi(`/tables/InventoryEquipType/DeleteInventoryEquipType?id=${itemId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Failed to delete inventory equipment type.');
  }

  return response;
}

export async function getAllInventoryBlacklistRecords(): Promise<InventoryEquipTypeBlacklist[]> {
  return json(
    await fetchApi('/api/v3/eo/inventory/blacklist', {
      headers: getHeaders(),
      method: 'GET',
    }),
  );
}

export async function getInventoryBlacklistRecordsForCustomer(customerId: string): Promise<InventoryEquipTypeBlacklist[]> {
  return json(
    await fetchApi(`/api/v3/eo/inventory/blacklist/customers/${customerId}`, {
      headers: getHeaders(),
      method: 'GET',
    }),
  );
}

export async function getInventoryBlacklistRecordsForEquipType(equipTypeId: string): Promise<InventoryEquipTypeBlacklist[]> {
  return json(
    await fetchApi(`/api/v3/eo/inventory/blacklist/${equipTypeId}`, {
      headers: getHeaders(),
      method: 'GET',
    }),
  );
}

export async function updateCustomersInventoryBlacklist(items: Partial<InventoryEquipTypeBlacklist>[]) {
  return json(
    await fetchApi('/api/v3/eo/inventory/blacklist', {
      headers: getHeaders(),
      method: 'POST',
      body: JSON.stringify(items),
    }),
  );
}

export async function getVendors(): Promise<Vendor[]> {
  return json(await fetchApi('/api/v3/eo/inventory/vendors', { method: 'GET' }));
}

export async function upsertVendor(vendor?: Vendor) {
  return await fetchApi('/api/v3/eo/inventory/vendors/', { body: JSON.stringify(vendor), method: 'POST' });
}

export async function deleteVendor(vendorId: string | undefined) {
  return await fetchApi(`/api/v3/eo/inventory/vendors/${vendorId}`, { method: 'DELETE' });
}

export function getPollworkerEvUser(id: string) {
  return fetchApi(`/tables/EVUser/GetEVUser?id=${id}`, {
    headers: getHeaders(),
    method: 'GET',
  }).then((response: Response) => response.json());
}

export async function getPollworkerWorkHistoryReportsTo(electionId: string): Promise<PollworkerUserInfo[]> {
  return fetchApi('/api/GetPollworkersWhoCanHaveReportsTo', {
    method: 'POST',
    body: JSON.stringify({ Id: electionId }),
  }).then((response: Response) => response.json());
}

export async function updatePollworkerEvUser(record: PollworkerUserInfo) {
  return fetchApi(`/tables/EVUser/PatchEVUser?id=${record.id}`, {
    method: 'PATCH',
    body: JSON.stringify({ ...record }),
  }).then((response: Response) => response.json());
}

export function updatePollworkerWorkHistory(record: { id: string; keyRoleId: string | undefined; reportsToId: string }) {
  return fetchApi(`/tables/PollworkerWorkHistory/${record.id}`, {
    headers: getHeaders(),
    method: 'PATCH',
    body: JSON.stringify({ ...record }),
  }).then((response: Response) => response.json());
}

export async function updatePollworkerWorkHistoryWorkConfirmation(
  pollworkerWorkHistoryWorkConfirmation: PollworkerWorkAssignmentConfirmationsForElection,
  status: WorkConfirmationStatusValue | number,
) {
  return fetchApi(`/tables/PollworkerWorkHistoryAssignmentConfirmation/${pollworkerWorkHistoryWorkConfirmation.id}`, {
    method: 'PATCH',
    body: JSON.stringify({
      id: pollworkerWorkHistoryWorkConfirmation.id,
      pollworkerWorkHistoryId: pollworkerWorkHistoryWorkConfirmation.pollworkerWorkHistoryId,
      electionId: pollworkerWorkHistoryWorkConfirmation.electionId,
      status: status,
    }),
  }).then((response: Response) => response.json());
}

export function createPollworkerWorkHistoryWorkConfirmation(electionId: string, pollworkerWorkHistoryId: string, status: number) {
  return fetchApi('/tables/PollworkerWorkHistoryAssignmentConfirmation', {
    headers: getHeaders(),
    method: 'POST',
    body: JSON.stringify({ electionId, pollworkerWorkHistoryId, status }),
  }).then((response: Response) => response.json());
}

export async function getPartiesForCustomer(customerId: string): Promise<Party[]> {
  return fetchApi(`/tables/Party/GetForCustomer/${customerId}`, {
    headers: getHeaders(),
    method: 'GET',
  }).then((response: Response) => response.json());
}

export async function updatePollworker(record: PollWorkerInfo) {
  return fetchApi(`/api/v3/pw/workers/${record.id}`, {
    method: 'PATCH',
    body: JSON.stringify({ ...record }),
  }).then((response: Response) => response.json());
}

export type PollworkerBulkUpdatePatch = {
  status?: number;
  okToSendSMS?: boolean;
  displayStateId?: string;
  electionDayWorker?: boolean;
  earlyVotingWorker?: boolean;
  defaultAssignedLocationId?: string;
  keyRegisteredPrecinctId?: string;
  keyRoleId?: string;
  reportsToId?: string;
  customFields?: { id: string; value: string | number | boolean | null }[];
};

export type PollworkerBulkUpdateItem = {
  id: string;
} & PollworkerBulkUpdatePatch;

export type PollworkerBulkUpdateResult = {
  success: boolean;
};

export async function bulkUpdatePollworkers(payload: PollworkerBulkUpdateItem[]): Promise<PollworkerBulkUpdateResult> {
  const response = await fetchApi('/api/v3/pw/workers', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok) {
    throw {
      response,
      data,
    };
  }

  return data;
}

export async function createEvUser(record: Partial<PollworkerUserInfo>) {
  return fetchApi('/tables/EVUser/PostEVUser', {
    method: 'POST',
    body: JSON.stringify(record),
  }).then((response: Response) => response.json());
}

export async function getPollworkerRoleTypes(): Promise<RoleInfo[]> {
  return fetchApi('/tables/PollworkerRole', {
    headers: getHeaders(),
    method: 'GET',
  }).then((response: Response) => response.json());
}

export function getPollworkerRoles(customerId: string) {
  return fetchApi('/api/GetPollworkerRoles', {
    headers: getHeaders(),
    method: 'POST',
    body: JSON.stringify({ Id: customerId }),
  }).then((response: Response) => response.json());
}

export function getBasePrecinctListForCustomer(customerId: string) {
  return fetchApi('/api/PrecinctApi/LoadBasePrecinctListForCustomer', {
    headers: getHeaders(),
    method: 'POST',
    body: JSON.stringify({ Id: customerId }),
  }).then((response: Response) => response.json());
}

export function getPollworkerSchedulesList(keyUserId: string, keyElectionId: string): Promise<DataburstResponse<Schedule[]>> {
  return fetchApi('/api/PollworkerScheduleApi/LoadPollworkerSchedulesList', {
    headers: getHeaders(),
    method: 'POST',
    body: JSON.stringify({ Id: keyUserId, ElectionId: keyElectionId }),
  })
    .then(async (response: Response) => (await response.json()) as { data: string; [key: string]: unknown })
    .then(
      (json) =>
        ({
          ...json,
          data: JSON.parse(atob(json.data)),
        }) as DataburstResponse<Schedule[]>,
    )
    .then((schedulesResponse) => {
      schedulesResponse.data = schedulesResponse.data.map((schedule) => ({
        ...schedule,
        workDate: dayjs.utc(schedule.workDate).format('LL'),
      }));
      return schedulesResponse;
    });
}

export interface WorkerSchedulesPageResponse {
  items: Schedule[];
  page: number;
  pageSize: number;
  totalCount: number;
  hasMore: boolean;
  upcomingCount: number;
  pastCount: number;
}

export function getWorkerSchedulesPage(
  evUserId: string,
  params: { electionId?: string; timeFilter?: 'upcoming' | 'past'; page: number; pageSize: number },
): [Promise<WorkerSchedulesPageResponse>, AbortController] {
  const searchParams = new URLSearchParams();
  if (params.electionId) searchParams.set('electionId', params.electionId);
  if (params.timeFilter) searchParams.set('timeFilter', params.timeFilter);
  searchParams.set('page', String(params.page));
  searchParams.set('pageSize', String(params.pageSize));

  const [request, controller] = fetchApiWithAbort(`/api/v3/pw/workers/${evUserId}/schedules?${searchParams.toString()}`);
  const response = request.then((r): Promise<WorkerSchedulesPageResponse> => r.json());

  return [response, controller];
}

export function deletePollworkerWorkHistory(keyPollworkerWorkHistoryId: string) {
  return fetchApi(`/tables/PollworkerWorkHistory/${keyPollworkerWorkHistoryId}`, {
    headers: getHeaders(),
    method: 'DELETE',
  });
}

export async function getPrecincts(): Promise<Precinct[]> {
  return fetchApi('/api/PrecinctApi/LoadSimplePrecinctList', {
    headers: getHeaders(),
    method: 'POST',
  })
    .then((response) => response.json())
    .then((response: DataburstResponse<string>) => {
      return JSON.parse(atob(response.data)) as Precinct[];
    });
}

export function getPrecinctsForElection(electionId: string): Promise<DataburstResponse<Precinct[]>> {
  return fetchApi('/api/PrecinctApi/LoadElectionPrecinctList', {
    headers: getHeaders(),
    method: 'POST',
    body: JSON.stringify({ Compress: false, Id: electionId }),
  })
    .then((response: Response) => response.json() as Promise<DataburstResponse<string>>)
    .then((response) => ({
      ...response,
      data: JSON.parse(atob(response.data)) as Precinct[],
    }));
}

export function getPrecinctsLoadCustomFields() {
  return fetchApi('/api/CustomFieldApi/LoadCustomFields', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      Id: null,
      ModuleId: 7,
      ObjectId: 401,
      Compress: false,
    }),
  })
    .then((response: Response) => response.json())
    .then(({ data }) => JSON.parse(atob(data)));
}

export function getVotingLocations(customerId: string) {
  return fetchApi('/api/VotingLocationApi/LoadCoreLocations', {
    headers: getHeaders(),
    method: 'POST',
    body: JSON.stringify({ Compress: false, Id: customerId }),
  }).then((response: Response) => response.json());
}

export function getVotingLocationsForElection(electionId: string): Promise<DataburstResponse<VotingLocation[]>> {
  return fetchApi('/api/VotingLocationApi/LoadListForElectionNoDocuments', {
    headers: getHeaders(),
    method: 'POST',
    body: JSON.stringify({ Compress: false, Id: electionId }),
  })
    .then((response: Response) => response.json() as Promise<DataburstResponse<string>>)
    .then((response) => ({
      ...response,
      data: JSON.parse(atob(response.data)) as VotingLocation[],
    }));
}

export async function loadPollworkerSchedulesForLocation(votingLocationId: string, electionId: string): Promise<DataburstResponse<VotingLocationSchedule[]>> {
  return fetchApi('/api/PollworkerScheduleApi/LoadPollworkerSchedulesForLocation', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ Id: votingLocationId, ElectionId: electionId }),
  })
    .then((resp) => resp.json() as Promise<DataburstResponse<string>>)
    .then((databurstResponse) => ({
      ...databurstResponse,
      data: JSON.parse(atob(databurstResponse.data)) as VotingLocationSchedule[],
    }))
    .then((databurstResponse) => ({
      ...databurstResponse,
      data: databurstResponse.data.map((schedule) => ({
        ...schedule,
        workDate: dayjs.utc(schedule.workDate).format('YYYY-MM-DD'),
      })),
    }));
}

export async function loadFullListForLocationWithAssignments(votingLocationId: string): Promise<DataburstResponse<VotingLocationWithAssignments[]>> {
  return fetchApi('/api/PollworkerLocationRoleApi/LoadFullListForLocationWithAssignments', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ Id: votingLocationId }),
  })
    .then((resp) => resp.json() as Promise<DataburstResponse<string>>)
    .then((databurstResponse) => ({
      ...databurstResponse,
      data: JSON.parse(atob(databurstResponse.data)) as VotingLocationWithAssignments[],
    }))
    .then((databurstResponse) => ({
      ...databurstResponse,
      data: databurstResponse.data.map((votingLocationWithAssignment) => ({
        ...votingLocationWithAssignment,
        assignments: votingLocationWithAssignment.assignments.map((assignment) => ({
          ...assignment,
          assignedDate: dayjs.utc(assignment.assignedDate).format('YYYY-MM-DD'),
        })),
      })),
    }));
}

export const getUser = (): Promise<{
  success?: boolean;
  message?: string;
  data: CurrentUserSettingsDTO | null;
}> => {
  return fetchApi('/api/v3/me', {
    headers: getHeaders(),
    method: 'GET',
  })
    .then((response: Response) => response.json())
    .then((resp) => ({
      success: true,
      data: resp,
    }));
};

export async function getAllWorkers(): Promise<PollWorker[]> {
  return fetchApi('/api/v3/pw/workers', {
    method: 'GET',
  }).then((response: Response) => response.json());
}

export async function getWorker(keyEvUserId: string): Promise<DataburstResponse<PollWorker>> {
  return fetchApi('/api/PollworkerApi/LoadPollworkerByEVUserId', {
    method: 'POST',
    body: JSON.stringify({
      id: keyEvUserId,
    }),
  })
    .then((response: Response) => response.json())
    .then((response: DataburstResponse<string>) => ({
      ...response,
      data: JSON.parse(atob(response.data)) as PollWorker,
    }));
}

export function getElectionWorkers(electionId: string, workerLevel: number, lastNameStartLetter = '*'): Promise<DataburstResponse<PollworkerApiResponse>> {
  return fetchApi('/api/PollworkerApi/LoadPollworkerWorkHistoryList', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      Compress: false,
      LastNameStartLetter: lastNameStartLetter,
      WorkerLevel: workerLevel,
      ElectionId: electionId,
    }),
  })
    .then((response: Response) => response.json() as Promise<DataburstResponse<string>>)
    .then((databurstResponse) => ({
      ...databurstResponse,
      data: JSON.parse(atob(databurstResponse.data)) as PollworkerApiResponse,
    }));
}

export function getElectionWorkersV3(electionId: string): Promise<PollWorker> {
  return fetchApi(`/api/v3/pw/workers?electionId=${encodeURIComponent(electionId)}`, {
    method: 'GET',
  }).then((response: Response) => response.json());
}

export function getPollworkersMasterList(electionId?: string, lastNameStartLetter = '*') {
  return fetchApi('/api/PollworkerApi/LoadPollworkersForCustomerWithWildCardAndAssignment', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      Compress: false,
      Id: lastNameStartLetter,
      Notes: electionId,
    }), // Yes, you read it right! Notes = ElectionId and Id = Wildcard
  }).then((response: Response) => response.json());
}

export function patchBulkUpdatePollworkerStatus(pollworkerIds: string[], status: number) {
  return fetchApi('/api/PollworkerApi/SetBulkPollworkerStatus', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      Compress: false,
      list: pollworkerIds,
      additionalDataInt32: status,
    }),
  }).then((response: Response) => response.json());
}

export function patchBulkUpdatePollworkerDisplayState(pollworkerIds: string[], displayStateId: string) {
  return fetchApi('/api/PollworkerApi/SetBulkPollworkerDisplayStates', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      Compress: false,
      list: pollworkerIds,
      id: displayStateId,
    }),
  }).then((response: Response) => response.json());
}

export function patchBulkUpdatePollworkerSms(pollworkerIds: string[], ok: boolean) {
  return fetchApi('/api/PollworkerApi/SetBulkOkToSendSMS', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      Compress: false,
      list: pollworkerIds,
      flagValue: ok,
    }),
  }).then((response: Response) => response.json());
}

export async function postSendSmsV3(messages: TwilioMessages) {
  return await fetchApiWithRetry('/api/v3/send-sms', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(messages),
  });
}

export async function postBulkSendSms(phoneNumberList: string[], message: string) {
  const response = await fetchApi('/api/BulkSendSMS', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ phoneNumberList, message }),
  });
  return await response.json();
}

export async function getPollworkerLoadCustomFields() {
  return fetchApi('/api/CustomFieldApi/LoadCustomFields', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      Id: null,
      ModuleId: 1,
      ObjectId: 201,
      Compress: false,
    }),
  })
    .then((response: Response) => response.json())
    .then(({ data }) => (data ? JSON.parse(atob(data)) : null));
}

export function getPollworkerLoadCustomFieldsDocument(keyPollworkerId: string) {
  return fetchApi('/api/CustomFieldContentApi/LoadCustomFieldContentForDocument', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      Id: keyPollworkerId,
      Compress: false,
      AdditionalDataInt32: 0,
    }),
  })
    .then((response: Response) => response.json())
    .then(({ data }) => data && JSON.parse(atob(data)));
}

export async function getPollworkerCustomFields(keyCustomerId: string): Promise<CustomFieldData[]> {
  return fetchApi('/api/CustomFieldContentApi/LoadCustomFieldDataWithIndex', {
    method: 'POST',
    body: JSON.stringify({
      Compress: false,
      Id: keyCustomerId,
      AdditionalData: '*',
      AdditionalDataInt32: 201,
    }),
  })
    .then((response: Response) => response.json())
    .then(({ data }) => (data ? JSON.parse(atob(data)) : []));
}

export async function getPollworkerWorkHistoryAssignmentConfirmationsForElection(electionId: string): Promise<PollworkerWorkAssignmentConfirmationsForElection[]> {
  return fetchApi('/api/GetPollworkerWorkHistoryAssignmentConfirmationsForElection', {
    method: 'POST',
    body: JSON.stringify({ Compress: false, Id: electionId }),
  })
    .then((response: Response) => response.json())
    .then(({ data }) => JSON.parse(atob(data)));
}

export function getPollworkerMessagesWithAbort(userId: string): [Promise<PollworkerMessage[]>, AbortController] {
  const [promise, controller] = fetchApiWithAbort('/api/GetMessagesFor', {
    method: 'POST',
    body: JSON.stringify({ Id: userId }),
  });

  return [promise.then((response: Response) => response.json()), controller];
}

export async function getPollworkerMessages(userId: string): Promise<PollworkerMessage[]> {
  const [promise] = getPollworkerMessagesWithAbort(userId);
  return promise;
}

export async function getPollworkerById(pollworker: PollworkerWorkHistory): Promise<PollworkerWorkHistory> {
  const { keyElectionId, keyEVUserId, workerLevel, userInfo } = pollworker;

  const { data } = await getElectionWorkers(keyElectionId, workerLevel, userInfo?.lastName?.[0]?.toLowerCase());

  return data.workerList.find((worker) => worker.keyEVUserId === keyEVUserId) as PollworkerWorkHistory;
}

export function getCustomerLogo(parentCustomerId: string) {
  return fetchApi('/api/BlobStorageApi/RetrieveCustomerLogo', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ Compress: false, Id: parentCustomerId }),
  })
    .then((resp) => resp.json())
    .then(({ data, success }) => {
      if (!success) return;

      return JSON.parse(atob(data)).data;
    });
}

export function getFileFromSecureStorage(uri: string): Promise<DataburstResponse<string>> {
  return fetchApi('/api/BlobStorageApi/RetrieveDataFileFromSecureStorage', {
    headers: getHeaders(),
    method: 'POST',
    body: JSON.stringify({
      Id: uri,
      Compress: false,
    }),
  }).then((resp) => resp.json());
}

export function getImageData(equipmentTypeId: string | undefined, customerIdOverride?: string, entitytype?: string, skipCache = false, updatedAt?: string, uri?: string) {
  if (!equipmentTypeId) return Promise.resolve(null);

  const cacheKey = imageCacheKey(equipmentTypeId, updatedAt);

  const cacheValue = !skipCache && Cache.get(cacheKey);
  if (cacheValue) return Promise.resolve(cacheValue);

  const id = [customerIdOverride || window.localStorage.getItem('customerId'), entitytype, equipmentTypeId].filter((p) => !!p).join('/');

  return fetchApi(`/api/BlobStorageApi/${uri || 'RetrieveDataFileFromSecureStorage'}`, {
    headers: getHeaders(),
    method: 'POST',
    priority: 'low',
    body: JSON.stringify({
      Id: id,
      Compress: false,
    }),
  })
    .then((response: Response) => response.json())
    .then((data) => {
      const parsedData = JSON.parse(atob(data.data));
      const payload = {
        id: equipmentTypeId,
        src: `data:image/jpg;base64,${parsedData?.data}`,
      };

      for (const key of Object.keys(Cache.all())) {
        if (key.startsWith(imageCachePrefix(equipmentTypeId)) && key !== cacheKey) {
          Cache.bust(key);
        }
      }

      return Cache.set(cacheKey, payload);
    })
    .catch((_err) => {
      return { id: equipmentTypeId, src: null };
    });
}

export function uploadAndCompressImage(imageFile: File, filePath: string) {
  const host = import.meta.env.VITE_API_V3_DOMAIN;

  const formData = new FormData();

  formData.append('file', imageFile);
  formData.append('fileName', filePath);

  return fetch(`${host}/api/v3/images/upload`, {
    headers: getAuthHeaders(),
    method: 'POST',
    body: formData,
  }).then((r) => r.json());
}

export function addToCart(item: ConsumableItem, quantity: number) {
  if (!quantity) return Promise.resolve();

  return fetchApi(`/api/Storeify/Orders/AddItemToCart?itemIdToAdd=${item.id}&quantity=${quantity}`, {
    headers: getHeaders(),
    method: 'POST',
    body: JSON.stringify({
      itemIdToAdd: item.id,
      quantity,
    }),
  })
    .then((response: Response) => response.json())
    .then(({ data, success }) => {
      if (success) {
        return JSON.parse(atob(data));
      }

      return data;
    });
}

export function getPollworkerApplicant(applicantId: string) {
  return fetchApi(`/api/PollworkerApplicantApi/${applicantId}`, {
    method: 'GET',
    headers: getHeaders(),
  }).then((response: Response) => response.json());
}

export async function createActionTemplate(actionTemplate: Partial<ActionTemplate>) {
  return fetchApi('/di-api/core/ActionEmailTemplate', {
    method: 'POST',
    body: JSON.stringify(actionTemplate),
  }).then((response: Response) => response.json());
}

export async function updateActionTemplate(actionTemplate: Partial<ActionTemplate>) {
  let updatedCustomerTemplate: Partial<ActionTemplate>;
  if (actionTemplate.fromUserId === DEFAULT_EMAIL_ID) {
    updatedCustomerTemplate = {
      ...actionTemplate,
      fromUserId: '',
    };
    return fetchApi(`/di-api/core/ActionEmailTemplate/${actionTemplate.id}`, {
      method: 'PUT',
      body: JSON.stringify(updatedCustomerTemplate),
    }).then((response: Response) => response.json());
  }
  return fetchApi(`/di-api/core/ActionEmailTemplate/${actionTemplate.id}`, {
    method: 'PUT',
    body: JSON.stringify(actionTemplate),
  }).then((response: Response) => response.json());
}

export async function removeEmailTemplateFile(customerId: string, templateId: string) {
  return fetchApi('/api/BlobStorageApi/RemoveFromBlobStorage', {
    method: 'POST',
    body: JSON.stringify({
      id: `${customerId}/emailtemplates/${templateId}.json`,
      compress: false,
    }),
  });
}

export async function deleteEmailTemplate(templateId: string) {
  return fetchApi(`/di-api/core/EmailTemplate/${templateId}`, {
    method: 'DELETE',
  });
}

export async function getEmailTemplate(customerId: string, templateId: string): Promise<EmailTemplateResponse | null> {
  const resp = await fetchApi('/api/BlobStorageApi/RetrieveDataFileFromSecureStorage', {
    method: 'POST',
    body: JSON.stringify({
      id: `${customerId}/emailtemplates/${templateId}.json`,
      compress: false,
    }),
  });

  const json = (await resp.json()) as DataburstResponse<string>;

  const step1 = safeParse<DataburstResponse<string>>(json.data);
  if (!step1) return null;

  const step2 = safeParse<EmailTemplateResponse>(step1.data);
  if (!step2) return null;

  return step2;
}

export async function updateEmailTemplate(customerTemplate: CustomerTemplate): Promise<DataburstResponse<string>> {
  let updatedCustomerTemplate: CustomerTemplate;
  if (customerTemplate.actionTemplate?.fromUserId === DEFAULT_EMAIL_ID) {
    updatedCustomerTemplate = {
      ...customerTemplate,
      actionTemplate: {
        ...customerTemplate.actionTemplate,
        fromUserId: '',
      },
    };
    return fetchApi(`/di-api/core/EmailTemplate/${customerTemplate.id}`, {
      method: 'PUT',
      body: JSON.stringify(updatedCustomerTemplate),
    }).then((resp) => resp.json());
  }
  return fetchApi(`/di-api/core/EmailTemplate/${customerTemplate.id}`, {
    method: 'PUT',
    body: JSON.stringify(customerTemplate),
  }).then((resp) => resp.json());
}

export async function createEmailTemplate(payload: Partial<CustomerTemplate>): Promise<CustomerTemplate> {
  return fetchApi('/di-api/core/EmailTemplate', {
    method: 'POST',
    body: JSON.stringify(payload),
  }).then((resp) => resp.json());
}

export async function updatePassword(newPassword: string, userId: string, loginUserId: string): Promise<DataburstResponse<string>> {
  return await fetchApi('/api/EVUserApi/UpdatePasswordAdminSimple', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      UserId: userId,
      NewPassword: newPassword,
      loginUserId: loginUserId,
    }),
  }).then((resp) => resp.json());
}

export async function beginPasswordReset(userId: string) {
  return await fetchApi('/api/EVUserApi/ResetPasswordSimple', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ UserId: userId }),
  }).then((resp) => resp.json());
}

export async function getAllCustomerBlogs(): Promise<CustomerBlog[]> {
  return fetchApi('/tables/CustomerBlog', {
    method: 'GET',
    headers: getHeaders(),
  }).then((resp) => resp.json());
}

export async function createCustomerBlog(blog: Omit<CustomerBlog, 'id' | 'dateCreated'>): Promise<CustomerBlog> {
  return fetchApi('/tables/CustomerBlog', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(blog),
  }).then((resp) => resp.json());
}

export async function updateCustomerBlog(id: string, updates: Partial<CustomerBlog>): Promise<CustomerBlog> {
  return fetchApi(`/tables/CustomerBlog/${id}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(updates),
  }).then((resp) => resp.json());
}

export async function deleteCustomerBlog(id: string): Promise<void> {
  await fetchApi(`/tables/CustomerBlog/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
}

export async function uploadToSecureBlobStorage(fileUrl: string, fileData: string | number[], documentName: string) {
  return fetchApiWithRetry('/api/BlobStorageApi/UploadDataFileToSecureStorage', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      Id: fileUrl,
      Data: fileData,
      DocumentName: documentName,
      Compress: false,
    }),
  }).then((resp) => resp.json());
}

export async function patchCustomField(customField: Partial<CustomField>) {
  return fetchApi(`/tables/CustomField/PatchCustomField/${customField.id}`, {
    method: 'PATCH',
    body: JSON.stringify(customField),
  }).then((resp) => resp.json());
}

export async function createCustomField(customField: Partial<CustomField>) {
  return fetchApi('/tables/CustomField/PostCustomField', {
    method: 'POST',
    body: JSON.stringify(customField),
  }).then((resp) => resp.json());
}

export async function deleteCustomField(customFieldId: string) {
  return fetchApi(`/tables/CustomField/DeleteCustomField?id=${customFieldId}`, {
    method: 'DELETE',
  });
}

export async function importPollWorkers(pollWorkers: PollworkerToImport[]): Promise<number> {
  return fetchApi('/api/v3/pw/workers/import', {
    method: 'POST',
    body: JSON.stringify(pollWorkers),
  }).then((resp) => resp.json());
}

export async function getPollworkerFlags() {
  return fetchApi('/tables/PollworkerDisplayState', {
    method: 'GET',
  }).then((resp) => resp.json());
}

export async function postPollworkerFlag(flag: PollworkerFlag) {
  return fetchApi('/tables/PollworkerDisplayState/PostPollworkerDisplayState', {
    method: 'POST',
    body: JSON.stringify(flag),
  }).then((resp) => resp.json());
}

export async function patchPollworkerFlag(flag: PollworkerFlag) {
  return fetchApi(`/tables/PollworkerDisplayState/PatchPollworkerDisplayState?id=${flag.id}`, {
    method: 'PATCH',
    body: JSON.stringify(flag),
  }).then((resp) => resp.json());
}

export async function deletePollworkerFlag(flag: PollworkerFlag) {
  return fetchApi(`/tables/PollworkerDisplayState/DeletePollworkerDisplayState?id=${flag.id}`, {
    method: 'DELETE',
  });
}

export async function loadRoleTrainingsForRole(roleId: string) {
  return await fetchApi('/api/PollworkerRoleTrainingApi/LoadRoleTrainingsForRole', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ id: roleId }),
  })
    .then((resp) => resp.json())
    .then((json) => JSON.parse(atob(json.data)));
}

export async function getTrainingsForCustomer(customerId: string): Promise<TrainingClass[]> {
  return await fetchApi(`/tables/PollworkerTrainingClass?$filter=${encodeURIComponent(`(KeyCustomerId eq '${customerId}')`)}`, {
    method: 'GET',
    headers: getHeaders(),
  }).then((resp) => resp.json());
}

export async function patchPollworkerTrainingCourse(
  formData: {
    keyCustomerId: string;
    className: string;
    keyEvUserContactId?: string;
    hasPrerequisite: boolean;
    prerequisiteId?: string;
    passValue: string;
    notes: string;
    certificateOfCompletionTemplateId: string | null;
    keyEVUserContactId?: string | undefined;
    createdAt?: string | undefined;
    id: string;
    updatedAt?: string | undefined;
    contactId?: string;
  },
  courseId: string,
) {
  return await fetchApi(`/tables/PollworkerTrainingClass/${courseId}`, {
    method: 'PATCH',
    body: JSON.stringify(formData),
  }).then((resp) => resp.json());
}

export async function getPollworkerClassById(classId: string) {
  return await fetchApi(`/tables/PollworkerTrainingClass/${classId}`, {
    method: 'GET',
  }).then((resp) => resp.json());
}

export async function addClassToRole(training: Partial<TrainingProgress>) {
  return await fetchApi('/tables/PollworkerRoleTraining', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ ...training }),
  }).then((resp) => resp.json());
}

export async function deleteClassFromRole(classId: string) {
  return await fetchApi(`/tables/PollworkerRoleTraining/${classId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
}

export async function doubleCheckRoleClassRequirements(roleId: string, electionId: string) {
  const resp = await fetchApi('/api/DoubleCheckRoleClassRequirements', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ id: roleId, notes: electionId }),
  });

  if (!resp.ok) {
    if (resp.status === 417) {
      throw new Error('There are no poll workers scheduled to this election on this role');
    }
    let errorMessage: string;
    try {
      const data = await resp.json();
      errorMessage = data.message || JSON.stringify(data);
    } catch {
      errorMessage = await resp.text();
    }
    throw new Error(errorMessage || `Request failed with status ${resp.status}`);
  }

  if (resp.status === 204 || resp.headers.get('content-length') === '0') {
    return null;
  }

  return resp.json();
}

export async function loadTimeBlocksForSection(sectionId: string) {
  return await fetchApi('/api/PollworkerClassSessionScheduleApi/LoadScheduleEntriesForSession', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ Id: sectionId }),
  })
    .then((resp) => resp.json())
    .then((encoded): TimeBlockResponseItem[] => JSON.parse(atob(encoded.data)));
}

export async function createTimeblockForPollworkerCourseSection(newTimeblock: Partial<TimeBlockResponseItem>): Promise<TimeBlockResponseItem> {
  return await fetchApi('/tables/PollworkerClassSessionSchedule/PostPollworkerClassSessionSchedule', {
    method: 'POST',
    body: JSON.stringify({ ...newTimeblock }),
  }).then((resp) => resp.json());
}

export async function patchTimeblockForPollworkerCourseSection(updatedTimeblock: Partial<TimeBlockResponseItem>): Promise<TimeBlockResponseItem> {
  return await fetchApi(`/tables/PollworkerClassSessionSchedule/PatchPollworkerClassSessionSchedule?id=${updatedTimeblock.id}`, {
    method: 'PATCH',
    body: JSON.stringify({ ...updatedTimeblock }),
  }).then((resp) => resp.json());
}

export async function rescheduleAttendeesToSection(
  attendeeIds: string[],
  sessionId: string,
  newSessionId: string,
): Promise<{
  rescheduledCount: number;
}> {
  return json<{
    rescheduledCount: number;
  }>(
    await fetchApi(`/api/v3/pw/trainings/${sessionId}/workers/reschedule/${newSessionId}`, {
      method: 'POST',
      body: JSON.stringify(attendeeIds),
    }),
  );
}

export async function validateRescheduleAttendees(attendeeIds: string[], sessionId: string, newSessionId: string): Promise<RescheduleWorkersValidationResult> {
  return json(
    await fetchApi(`/api/v3/pw/trainings/${sessionId}/workers/reschedule/validate/${newSessionId}`, {
      method: 'POST',
      body: JSON.stringify(attendeeIds),
    }),
  );
}

export async function pollworkerBulkSendEmail(payload: PollworkerEmailQueryParams) {
  return await fetchApi('/api/EmailApi/SendPollworkerBulkEmailMessages', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
    .then((resp) => resp.json())
    .then((resp) => atob(resp.data));
}

export async function getMergeFieldDtoForEmail(
  payload: { EVUserTargetIds: string[]; customerId: string; userId: string; electionId?: string },
  timeZoneOffset: number,
): Promise<PollworkerMergeWorker[] | CampaignFinanceMergeFixtures[]> {
  return await fetchApi(`/di-api/email/BuildEmailMergeDto?timeZoneOffset=${timeZoneOffset}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }).then((resp) => resp.json());
}

export async function loadPollworkerScheduleReportData(electionId: string): Promise<PollworkerScheduleReportDTO[]> {
  return await fetchApi('/api/PollworkerApi/LoadPollworkerScheduleReportData', {
    method: 'POST',
    body: JSON.stringify({ Id: electionId }),
  })
    .then((resp) => resp.json())
    .then((resp) => JSON.parse(atob(resp.data)) as PollworkerScheduleReportDTO[]);
}

export async function loadPollworkerAssignmentReportData(electionId: string): Promise<PollworkerAssignmentReportDTO[]> {
  return await fetchApi('/api/PollworkerApi/LoadPollworkerAssignmentReportData', {
    method: 'POST',
    body: JSON.stringify({ Id: electionId, Compress: false }),
  })
    .then((resp) => resp.json())
    .then((resp) => JSON.parse(atob(resp.data)));
}

export async function loadPollworkerClassScheduleReportData(electionId: string, customerId: string): Promise<PollworkerClassScheduleReportDTO[]> {
  return await fetchApi('/api/PollworkerApi/LoadPollworkerClassScheduleReportData', {
    method: 'POST',
    body: JSON.stringify({ ElectionId: electionId, Compress: false, Id: customerId }),
  })
    .then((resp) => resp.json())
    .then((resp) => JSON.parse(atob(resp.data)));
}

export async function loadPollworkerApplicants(): Promise<WorkerApplication[]> {
  return await fetchApi('/api/PollworkerApplicantApi/LoadPollworkerApplicants', {
    method: 'POST',
    body: JSON.stringify({ LastNameStartLetter: '*' }),
  })
    .then((resp) => resp.json())
    .then((resp) => {
      const decoded = atob(resp.data);
      return JSON.parse(decoded);
    });
}

export async function patchPollworkerApplication(applicationId: string, status: number) {
  return await fetchApi(`/tables/PollworkerApplicant/PatchPollworkerApplicant?id=${applicationId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status: status }),
  }).then((resp) => resp.json());
}

export async function deletePollworkerApplication(applicationId: string) {
  return await fetchApi(`/tables/PollworkerApplicant/DeletePollworkerApplicant?id=${applicationId}`, {
    method: 'DELETE',
  });
}

export async function acceptPollworkerApplication(applicationId: string) {
  return await fetchApi('/api/PollworkerApplicantApi/AcceptPollworkerCandidate', {
    method: 'POST',
    body: JSON.stringify({ Id: applicationId }),
  });
}

export async function bulkArchivePollworkers(workerIds: string[]) {
  return await fetchApi('/api/v3/pw/workers/archive', {
    method: 'POST',
    body: JSON.stringify({ WorkerIds: workerIds }),
  });
}
